/**
 * AdminAudiencesPage — Module 8 Phase C directory.
 *
 * Lists every system-managed audience with its live member count. Grouped
 * by category. Clicking a card opens a modal with the audience's members
 * (read-only). Master admins can trigger a global reconcile.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Users, RefreshCw, Loader2, Search, MessageSquare, Mail,
  Calendar, Building2, Home, Wrench, MapPin, PhoneCall
} from 'lucide-react';

import AudienceGroups from '../../components/admin/AudienceGroups';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  communication:      { label: 'Communication Status', icon: MessageSquare, tone: 'text-[#F56F05]' },
  frequency:          { label: 'Frequency',            icon: Calendar,       tone: 'text-blue-600'  },
  customer_type:      { label: 'Customer Type',        icon: Building2,      tone: 'text-emerald-600' },
  property_type:      { label: 'Property Type',        icon: Home,           tone: 'text-purple-600' },
  service:            { label: 'Service',              icon: Wrench,         tone: 'text-orange-600' },
  lead_source:        { label: 'Lead Source',          icon: MapPin,         tone: 'text-rose-600' },
  preferred_contact:  { label: 'Preferred Contact',    icon: PhoneCall,      tone: 'text-teal-600' },
};

const CATEGORY_ORDER = [
  'communication', 'frequency', 'customer_type', 'property_type', 'service', 'lead_source', 'preferred_contact',
];

const AdminAudiencesPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState([]);
  const [totals, setTotals] = useState({ audiences: 0, memberships: 0, byCategory: {} });
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [search, setSearch] = useState('');
  const [membersModal, setMembersModal] = useState({ open: false, audience: null, members: [], loading: false });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isMasterAdmin = user?.role === 'master_admin';

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/communication/audiences/directory`, { headers });
      setAudiences(r.data?.audiences || []);
      setTotals({
        audiences: r.data?.total_audiences || 0,
        memberships: r.data?.total_memberships || 0,
        byCategory: r.data?.by_category || {},
      });
    } catch (err) {
      toast.error('Failed to load audience directory');
      setAudiences([]);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchDirectory(); }, [fetchDirectory]);

  const runReconcile = async () => {
    if (!isMasterAdmin) return;
    setReconciling(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/communication/audiences/reconcile`, {}, { headers });
      const rep = r.data?.report || {};
      toast.success(`Reconciled ${rep.examined || 0} people — ${rep.changed || 0} changed, ${rep.unchanged || 0} unchanged.`);
      fetchDirectory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed');
    } finally { setReconciling(false); }
  };

  const openMembers = async (audience) => {
    setMembersModal({ open: true, audience, members: [], loading: true });
    try {
      const r = await axios.get(`${API_URL}/api/admin/communication/audiences/${audience.id}/members?limit=200`, { headers });
      setMembersModal({ open: true, audience, members: r.data?.members || [], loading: false });
    } catch (err) {
      setMembersModal({ open: true, audience, members: [], loading: false });
      toast.error('Failed to load members');
    }
  };

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = {};
    for (const a of audiences) {
      if (q && !a.label.toLowerCase().includes(q) && !a.rule_key.toLowerCase().includes(q)) continue;
      (out[a.category] = out[a.category] || []).push(a);
    }
    return out;
  }, [audiences, search]);

  return (
    <div className="min-h-screen" data-testid="admin-audiences-page">
      <header className="gg-legacy-head">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-heading text-zinc-900">Audiences</h1>
          </div>
          <Button size="sm" variant="outline" onClick={fetchDirectory} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isMasterAdmin && (
            <Button size="sm" className="bg-[#F56F05] hover:bg-[#C2410C] text-white"
              onClick={runReconcile} disabled={reconciling} data-testid="reconcile-all-btn">
              {reconciling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Reconcile All
            </Button>
          )}
        </div>
      </header>


      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <AudienceGroups headers={headers} />
        <div className="pt-4"><p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">AUTOMATIC AUDIENCES</p><p className="text-xs text-[#77776e] mt-1">Kept in sync from CRM records. Read-only.</p></div>
        {/* Overview strip */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-6 flex-wrap">
            <div>
              <p className="text-[10px] uppercase text-zinc-400 font-semibold">System audiences</p>
              <p className="text-2xl font-bold text-zinc-900" data-testid="total-audiences">{totals.audiences}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-zinc-400 font-semibold">Total memberships</p>
              <p className="text-2xl font-bold text-[#F56F05]" data-testid="total-memberships">{totals.memberships}</p>
            </div>
            <div className="text-[11px] text-zinc-400 italic flex-1 min-w-[160px] max-w-[260px] leading-relaxed">
              Auto-synced from canonical CRM data on every consent, appointment, or profile change. Read-only.
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search audiences…" className="pl-9" data-testid="search-audiences" />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="py-16 text-center text-zinc-400"><Loader2 className="h-6 w-6 inline animate-spin mr-2" />Loading directory…</div>
        ) : (
          CATEGORY_ORDER.map(cat => {
            const rows = grouped[cat];
            if (!rows || rows.length === 0) return null;
            const meta = CATEGORY_META[cat];
            const Icon = meta?.icon || Users;
            return (
              <section key={cat} data-testid={`category-section-${cat}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <h2 className="text-sm font-bold text-zinc-800 flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${meta?.tone || 'text-zinc-500'}`} />
                    {meta?.label || cat}
                  </h2>
                  <span className="text-[11px] text-zinc-500" data-testid={`category-total-${cat}`}>
                    {totals.byCategory[cat] || 0} members across {rows.length} audience{rows.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {rows.map(a => (
                    <button key={a.id} type="button" onClick={() => openMembers(a)}
                      className="text-left"
                      data-testid={`audience-card-${a.rule_key}`}>
                      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                        <CardContent className="p-4 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-zinc-900 truncate">{a.label}</p>
                            <p className="text-[10px] font-mono text-zinc-400 truncate">{a.rule_key}</p>
                          </div>
                          <Badge variant="outline" className={`flex-shrink-0 text-sm font-bold px-2.5 py-1 ${a.member_count > 0 ? 'bg-orange-50 text-[#C2410C] border-orange-200' : 'bg-zinc-100 text-zinc-500 border-zinc-200'}`}
                            data-testid={`audience-count-${a.rule_key}`}>
                            {a.member_count}
                          </Badge>
                        </CardContent>
                      </Card>
                    </button>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </main>

      <MembersModal
        state={membersModal}
        onClose={() => setMembersModal(m => ({ ...m, open: false }))}
        navigate={navigate}
      />
    </div>
  );
};

const MembersModal = ({ state, onClose, navigate }) => {
  const { open, audience, members, loading } = state;
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="audience-members-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#F56F05]" />
            {audience?.label || 'Audience'}
          </DialogTitle>
          <DialogDescription>
            {audience?.rule_key} &middot; {members.length} member{members.length === 1 ? '' : 's'} &middot; Read-only
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mr-2 pr-2">
          {loading ? (
            <div className="py-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
          ) : members.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-500 italic">No one is in this audience yet.</p>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="members-list">
              {members.map(m => (
                <li key={m.person_id} className="py-2 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {m.display_name}
                      {m.is_lead && <span className="ml-2 text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">LEAD</span>}
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate">{m.email || '—'}</p>
                  </div>
                  {!m.is_lead && (
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]"
                      onClick={() => { onClose(); navigate(`/admin/customers/${m.person_id}`); }}
                      data-testid={`view-member-${m.person_id}`}>
                      View
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="text-[10px] text-zinc-400 italic pt-2 border-t border-zinc-100">
          Memberships auto-sync from canonical CRM data &mdash; you cannot manually add or remove people here.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAudiencesPage;
